import React, { Fragment, useState, useMemo } from "react";
import { ChevronDown, Lock, Search, X } from "lucide-react";
import { Listbox, Transition } from "@headlessui/react";

type HatBoxProps = {
  hatName: string | null;
  availableHats?: Array<{ id?: string; hat_name: string; }>;
  onHatChange?: (hatId: string) => void;
};

const HatBox: React.FC<HatBoxProps> = ({ hatName, availableHats = [], onHatChange }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredHats = useMemo(() => {
    if (!searchQuery.trim()) return availableHats;
    
    return availableHats.filter(hat => {
      if (!hat || !hat.hat_name || typeof hat.hat_name !== 'string') {
        return false;
      }
      return hat.hat_name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [availableHats, searchQuery]);

    if (!availableHats.length || !onHatChange) {
    return (
      <div className="w-48 sm:w-48 lg:w-60">
        <div className="relative mt-1">
          <div className="relative w-full rounded-lg bg-surface-overlay shadow-sm py-2 pl-3 pr-10 text-left cursor-not-allowed opacity-70">
            <span className="flex items-center justify-between font-medium text-primary">
              <img
                src="/va-icon-bw.svg"
                alt="Virtual Assistant Icon"
                className="w-5 h-5"
              />
              <span className="truncate flex-1 pl-2">
                {hatName || "No Virtual Assistant"}
              </span>
              <Lock className="h-4 w-4 text-subtle mr-2" />
            </span>
          </div>
        </div>
      </div>
    );
  }

    const selectedHat = hatName 
    ? availableHats.find(hat => hat && hat.hat_name === hatName)
    : null;

  const handleHatSelect = (hatId: string) => {
    if (onHatChange) {
      onHatChange(hatId);
    }
    setSearchQuery(""); 
    setIsOpen(false);
  };

  return (
    <div className="w-48 sm:w-48 lg:w-60">
      <Listbox 
        value={selectedHat?.id || ''} 
        onChange={handleHatSelect}
      >
        {({ open }) => {
          // Update local state when Listbox opens/closes
          if (open !== isOpen) {
            setIsOpen(open);
            if (!open) setSearchQuery(""); 
          }
          
          return (
            <div className="relative mt-1">
              <Listbox.Button className="relative w-full rounded-lg bg-surface-overlay shadow-sm hover:bg-surface-2 hover:shadow py-2 pl-3 pr-10 text-left cursor-pointer transition-all duration-200">
                <span className="flex items-center justify-between font-medium text-primary">
                  <img
                    src="/va-icon-bw.svg"
                    alt="Virtual Assistant Icon"
                    className="w-5 h-5"
                  />
                  <span className={`truncate flex-1 pl-2 ${!hatName ? 'text-subtle italic' : ''}`}>
                    {hatName || "Select Virtual Assistant..."}
                  </span>
                  <ChevronDown className="h-4 w-4 text-subtle mr-2" />
                </span>
             </Listbox.Button>

             <Transition
               as={Fragment}
               leave="transition ease-in duration-100"
               leaveFrom="opacity-100"
               leaveTo="opacity-0"
               afterLeave={() => setSearchQuery("")}
              >
                <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md bg-surface-overlay text-primary shadow-lg ring-1 ring-border-default focus:outline-none">
                  {/* Search Input */}
                  <div className="sticky top-0 z-10 bg-surface-overlay border-b border-border-default p-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-subtle" />
                      <input
                        type="text"
                        placeholder="Search Virtual Assistants..."
                        className="w-full pl-10 pr-3 py-2 text-sm bg-surface-2 text-primary border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent placeholder:text-subtle"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()} 
                      />
                    </div>
                  </div>

                  {/* Options List */}
                 <div className="max-h-48 overflow-auto py-1">
                   {/* Add "No Virtual Assistant" option at the top */}
                   <Listbox.Option
                     value=""
                     className={({ active }) =>
                       `relative cursor-pointer select-none py-2 pl-10 pr-4 text-primary ${
                         active ? 'bg-surface-2' : ''
                       }`
                     }
                   >
                    {({ selected }) => (
                      <>
                        <span className={`block truncate italic text-subtle ${selected ? 'font-medium' : 'font-normal'}`}>
                          No Virtual Assistant
                       </span>
                       {selected && !hatName && (
                         <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-subtle">
                           <X className="w-4 h-4" />
                         </span>
                       )}
                     </>
                   )}
                 </Listbox.Option>

                 {/* hat options */}
                 {filteredHats.length > 0 ? (
                   filteredHats.map((hat) => (
                     <Listbox.Option
                       key={hat.id || hat.hat_name}
                       value={hat.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 text-primary ${
                              active ? 'bg-surface-2' : ''
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {hat.hat_name || 'Unnamed Virtual Assistant'}
                              </span>
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                                  <img
                                    src="/va-icon-bw.svg"
                                    alt="Selected"
                                    className="w-4 h-4"
                                  />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))
                    ) : (
                      <div className="py-4 px-4 text-sm text-subtle text-center">
                        No Virtual Assistants found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </Listbox.Options>
              </Transition>
            </div>
          );
        }}
      </Listbox>
    </div>
  );
};

export default HatBox;